// AdminAppIcons — platform-admin page for setting the home-screen
// icon of each installable PWA (player / admin / agent).
//
// Three cards, one per app. Each shows the current 512² icon (or a
// "no icon yet" placeholder), file size + last-uploaded time, and a
// Replace / Upload button. Picking a file pops the AvatarCropper for
// drag-and-zoom centering before upload.
//
// Rebuilt in light + dark new aesthetic.
//
// Phase 4 UI overhaul (2026-05-15): mounted inside AccountHub as the
// "App icons" sub-tab — the hub already provides the outer max-w, the
// "Account" page title, and the sub-tab strip. This panel now drops
// its own outer wrapper, the stale "Back to Account" link (the hub
// has a proper tab to switch back), and the duplicated "App icons"
// page header. Surface tokens align with Phase 2/3: arena ring-1
// ring-inset on every card, arena/neon text scale, violet admin
// accent. Multipart upload via `fetch` + getAdminAccessToken is
// preserved verbatim (per-app POST /api/admin/app-icons/:app).

import { useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';
import AvatarCropper from '@baaligames/shared-ui/components/AvatarCropper';
import DangerConfirmModal from '@baaligames/shared-ui/components/DangerConfirmModal';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi, getAdminAccessToken } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import { compressForUpload } from '@baaligames/shared-ui/lib/imageCompress';
import MediaGalleryModal, { MediaAsset } from '@baaligames/shared-ui/admin/components/MediaGalleryModal';
import { useAdminAuth } from '@baaligames/shared-ui/admin/lib/adminAuth';

type AppName = 'player' | 'admin' | 'agent';

type Status = {
  present: boolean;
  size_bytes?: number;
  mtime?: string;
  preview_url: string | null;
};

type ListResp = { apps: Record<AppName, Status> };

const APP_META: Record<AppName, {
  label: string; subtitle: string; icon: string; previewBase: string;
  iconBg: string; iconText: string; ring: string;
}> = {
  player: {
    label:       'Baali Games (player)',
    subtitle:    'The home-screen app players install at /',
    icon:        'sports_esports',
    previewBase: '/icons/icon-512.png',
    iconBg:      'bg-amber-50 dark:bg-amber-400/15',
    iconText:    'text-amber-700 dark:text-amber-300',
    ring:        'ring-amber-200/60 dark:ring-amber-400/30',
  },
  admin: {
    label:       'Baali Admin',
    subtitle:    'Your admin console at /admin/login',
    icon:        'shield',
    previewBase: '/icons-admin/icon-512.png',
    iconBg:      'bg-violet-50 dark:bg-violet-400/15',
    iconText:    'text-violet-700 dark:text-violet-300',
    ring:        'ring-violet-200/60 dark:ring-violet-400/30',
  },
  agent: {
    label:       'Baali Agent',
    subtitle:    'Agent portal at /agent/login',
    icon:        'support_agent',
    previewBase: '/icons-agent/icon-512.png',
    iconBg:      'bg-emerald-50 dark:bg-emerald-400/15',
    iconText:    'text-emerald-700 dark:text-emerald-300',
    ring:        'ring-emerald-200/60 dark:ring-emerald-400/30',
  },
};

function formatKB(b?: number) {
  if (!b && b !== 0) return '—';
  if (b < 1024) return `${b} B`;
  return `${(b / 1024).toFixed(1)} KB`;
}

export default function AppIconsPanel() {
  const { admin } = useAdminAuth();
  const [data, setData]         = useState<Record<AppName, Status> | null>(null);
  const [busy, setBusy]         = useState<AppName | null>(null);
  const [toast, setToast]       = useState<{ tone: 'ok' | 'err'; msg: string } | null>(null);
  const [bump, setBump]         = useState(Date.now());
  const [cropping, setCropping] = useState<{ app: AppName; file: File } | null>(null);
  // Batch 3 #32: target of the Remove-icon confirmation modal.
  const [removing, setRemoving] = useState<AppName | null>(null);
  // 2026-05-20 fast-follow — Media Library picker state. Stores
  // which app is currently choosing an icon, so on-pick we know
  // which canonical master to attach to.
  const [galleryApp, setGalleryApp] = useState<AppName | null>(null);

  async function load() {
    try {
      const r = await adminApi.get<ListResp>('/admin/app-icons');
      setData(r.apps);
    } catch (e) {
      flash('err', (e as ApiError).message || 'Could not load icon status');
    }
  }
  useEffect(() => { load(); }, []);

  function flash(tone: 'ok' | 'err', msg: string) {
    setToast({ tone, msg });
    setTimeout(() => setToast(null), 3500);
  }

  // Batch 3 #32: actual remove call. Uses adminApi.delete since the
  // wire format matches POST/GET (no multipart needed for a delete).
  async function remove(app: AppName) {
    setBusy(app);
    try {
      await adminApi.del(`/admin/app-icons/${app}`);
      await load();
      setBump(Date.now());  // bust cached preview thumbs
      setRemoving(null);
      flash('ok', `${APP_META[app].label} icon removed.`);
    } catch (e) {
      flash('err', (e as ApiError).message || 'Remove failed');
      throw e;  // re-raise so DangerConfirmModal stays open with error
    } finally {
      setBusy(null);
    }
  }

  // Multipart upload — preserved exactly. Uses native fetch (not
  // adminApi) because we need to set the Bearer header manually and
  // attach FormData. Endpoint shape unchanged: POST /api/admin/app-icons/:app.
  async function upload(app: AppName, rawFile: File) {
    setBusy(app);
    try {
      // 2026-05-20 fast-follow — client-side compression in front of
      // the existing variant-tree pipeline. Sources can be 4096px+
      // designer PNGs; the preset clamps to 1024px / 0.5MB before
      // upload while preserving the alpha channel.
      const file = await compressForUpload(rawFile, { preset: 'app_icon' });
      const fd = new FormData();
      fd.append('image', file);
      const t = getAdminAccessToken();
      const res = await fetch(`/api/admin/app-icons/${app}`, {
        method: 'POST', credentials: 'include',
        headers: t ? { authorization: `Bearer ${t}` } : {},
        body: fd,
      });
      const j = await res.json();
      if (!j.ok) throw new Error(j.error?.message || 'Upload failed');
      await load();
      setBump(Date.now());
      flash('ok', `${APP_META[app].label} icon updated — installed copies of the app keep the old icon until reinstalled.`);
    } catch (e) {
      flash('err', (e as Error).message || 'Upload failed');
    } finally {
      setBusy(null);
    }
  }

  // 2026-05-20 fast-follow — attach a library asset as the canonical
  // master for the named app. Hits PATCH /api/admin/app-icons/:app
  // which reads the library file's bytes and re-runs the full variant
  // generator on the receiving side.
  async function attachLibrary(app: AppName, asset: MediaAsset) {
    setGalleryApp(null);
    setBusy(app);
    try {
      await adminApi.patch(`/admin/app-icons/${app}`, { url: asset.url });
      await load();
      setBump(Date.now());
      flash('ok', `${APP_META[app].label} icon swapped to a previous library asset.`);
    } catch (e) {
      flash('err', (e as ApiError).message || 'Library attach failed');
    } finally {
      setBusy(null);
    }
  }

  if (admin?.role !== 'admin') {
    return (
      <div className="rounded-2xl bg-rose-50 dark:bg-rose-500/10
                      ring-1 ring-inset ring-rose-200/60 dark:ring-rose-400/30
                      text-rose-800 dark:text-rose-200 p-8 text-center">
        <Icon name="lock" size={28} className="mx-auto mb-2" />
        <div className="font-bold">Admin only</div>
      </div>
    );
  }

  function pickFile(app: AppName, file: File) {
    setCropping({ app, file });
  }

  return (
    // Phase 4 (2026-05-15): no outer max-w wrapper, no Back-to-Account
    // link, no duplicate "App icons" h1 — AccountHub provides all three.
    <div className="space-y-4">
      {/* Help banner — sky-tinted, matches the agent-end pattern of
          info banners (ring-1 ring-inset arena-tinted variants). */}
      <div className="rounded-2xl
                      bg-sky-50 dark:bg-sky-500/10
                      ring-1 ring-inset ring-sky-200/60 dark:ring-sky-400/30
                      text-sky-800 dark:text-sky-200
                      px-4 py-3 text-[12px] leading-relaxed">
        <Icon name="info" size={14} className="inline -mt-0.5 mr-1" />
        Pick any high-resolution image — PNG, JPG, or WebP. After you choose a file, drag &amp;
        zoom to centre it, then save. We auto-generate <b>9 size variants</b> + maskable icon
        + favicon + iOS apple-touch-icon. Already-installed copies keep the old icon until reinstalled.
      </div>

      {toast && (
        <div className={`rounded-2xl ring-1 ring-inset px-4 py-3 text-[13px]
          ${toast.tone === 'ok'
            ? 'bg-emerald-50 ring-emerald-200/60 text-emerald-800 dark:bg-emerald-500/10 dark:ring-emerald-400/30 dark:text-emerald-200'
            : 'bg-rose-50 ring-rose-200/60 text-rose-800 dark:bg-rose-500/10 dark:ring-rose-400/30 dark:text-rose-200'}`}>
          {toast.msg}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {(Object.keys(APP_META) as AppName[]).map((appName) => (
          <AppIconCard
            key={appName}
            appName={appName}
            status={data?.[appName]}
            busy={busy === appName}
            bump={bump}
            onPickFile={(f) => pickFile(appName, f)}
            onPickLibrary={() => setGalleryApp(appName)}
            onRemove={() => setRemoving(appName)}
          />
        ))}
      </div>

      {/* Batch 3 #32: Remove icon — DangerConfirmModal with typed
          confirm. Removes the master + every generated variant for
          this app. Already-installed PWAs keep their cached icon. */}
      {removing && (
        <DangerConfirmModal
          open={true}
          onClose={() => setRemoving(null)}
          onConfirm={() => remove(removing)}
          title={`Remove ${APP_META[removing].label} icon?`}
          description={
            <>
              Deletes the canonical master + every generated size variant for the{' '}
              <span className="font-semibold">{APP_META[removing].label}</span> app. Already-installed PWAs keep
              the icon they were installed with (browser cache); fresh installs fall back to the framework
              default until a new master is uploaded.
            </>
          }
          confirmLabel="Remove"
          cancelLabel="Keep icon"
          confirmWord="REMOVE"
        />
      )}

      {/* 2026-05-20 fast-follow — Media Library picker. Filter scoped
          to intended_use='app_icon' so the gallery only shows past
          masters that are roll-back targets, not unrelated assets. */}
      {galleryApp && (
        <MediaGalleryModal
          intendedUse="app_icon"
          title={`Choose icon master — ${APP_META[galleryApp].label}`}
          onPick={(asset) => attachLibrary(galleryApp, asset)}
          onClose={() => setGalleryApp(null)}
        />
      )}

      {cropping && (
        <AvatarCropper
          title={`Adjust icon — ${APP_META[cropping.app].label}`}
          initialFile={cropping.file}
          outputSize={1024}
          outputFormat="image/png"
          outputFilename="icon.png"
          hint="Drag to position · pinch / scroll / slider to zoom · output is 1024×1024 PNG; we resize down to every size the platform needs."
          onUpload={async (file) => {
            await upload(cropping.app, file);
          }}
          onClose={() => setCropping(null)}
        />
      )}
    </div>
  );
}

function AppIconCard({ appName, status, busy, bump, onPickFile, onPickLibrary, onRemove }: {
  appName: AppName;
  status?: Status;
  busy: boolean;
  bump: number;
  onPickFile: (file: File) => void;
  /** 2026-05-20 fast-follow — open the Media Library picker. */
  onPickLibrary: () => void;
  onRemove: () => void;  // Batch 3 #32
}) {
  const meta = APP_META[appName];
  const fileRef = useRef<HTMLInputElement>(null);
  const present = !!status?.present;
  const previewUrl = present ? `${meta.previewBase}?t=${bump}` : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className={`rounded-2xl p-4
                  bg-white dark:bg-arena-700/40
                  ring-1 ring-inset ${meta.ring}
                  shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none
                  backdrop-blur`}
    >
      <div className="flex items-start gap-4">
        {/* Preview tile */}
        <div className="relative shrink-0">
          <div className="w-24 h-24 rounded-2xl
                          bg-arena-100/60 dark:bg-arena-900/60
                          ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                          overflow-hidden grid place-items-center">
            {previewUrl ? (
              <img src={previewUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              <Icon name={meta.icon} size={40} className="text-arena-300 dark:text-neon-200/30" />
            )}
          </div>
          {present && (
            <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-emerald-500 grid place-items-center shadow-md">
              <Icon name="check" size={14} className="text-white" />
            </span>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className={`grid place-items-center w-7 h-7 rounded-lg ${meta.iconBg} ${meta.iconText}`}>
              <Icon name={meta.icon} size={15} filled />
            </span>
            <h3 className="text-[14px] font-semibold text-arena-900 dark:text-white truncate">{meta.label}</h3>
          </div>
          <p className="text-[11.5px] text-arena-600 dark:text-neon-200/60 mt-0.5">{meta.subtitle}</p>

          <div className="mt-2 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-arena-600 dark:text-neon-200/60">
            {present ? (
              <>
                <span className="inline-flex items-center gap-1">
                  <Icon name="folder" size={12} />
                  master {formatKB(status?.size_bytes)}
                </span>
                {status?.mtime && (
                  <span className="inline-flex items-center gap-1">
                    <Icon name="schedule" size={12} />
                    {new Date(status.mtime).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                  </span>
                )}
              </>
            ) : (
              <span className="text-amber-700 dark:text-amber-300 inline-flex items-center gap-1">
                <Icon name="warning" size={12} /> No icon set — using default
              </span>
            )}
          </div>

          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-full
                         bg-violet-600 hover:bg-violet-500 active:bg-violet-700
                         text-white text-[12px] font-bold px-3 py-1.5
                         shadow-[0_2px_10px_rgba(139,92,246,0.4)]
                         transition active:scale-95 disabled:opacity-50"
            >
              {busy
                ? <Icon name="progress_activity" size={13} className="animate-spin" />
                : <Icon name={present ? 'cached' : 'upload'} size={13} />}
              {busy ? 'Generating…' : (present ? 'Replace icon' : 'Upload icon')}
            </button>
            {/* 2026-05-20 fast-follow — Pick from Library button.
                Opens the gallery filtered to intended_use='app_icon'.
                The chosen master gets reattached server-side via
                PATCH /api/admin/app-icons/:app. */}
            <button
              type="button"
              onClick={onPickLibrary}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-full
                         bg-violet-50 hover:bg-violet-100
                         dark:bg-violet-500/10 dark:hover:bg-violet-500/20
                         text-violet-700 dark:text-violet-200
                         text-[12px] font-bold px-3 py-1.5
                         ring-1 ring-inset ring-violet-400/40
                         transition active:scale-95 disabled:opacity-50"
            >
              <Icon name="photo_library" size={13} /> Library
            </button>
            {present && previewUrl && (
              <a
                href={previewUrl} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 text-[11px] font-semibold
                           text-arena-600 dark:text-neon-200/60 hover:text-arena-900
                           dark:text-neon-200/60 dark:hover:text-white transition"
              >
                <Icon name="open_in_new" size={12} /> View 512²
              </a>
            )}
            {/* Batch 3 #32: Remove icon (only visible when an icon is
                actually present — nothing to remove otherwise). Click
                opens the DangerConfirmModal with typed confirm. */}
            {present && (
              <button
                type="button"
                onClick={onRemove}
                disabled={busy}
                className="inline-flex items-center gap-1 text-[11px] font-semibold
                           text-rose-600 hover:text-rose-700
                           dark:text-rose-400 dark:hover:text-rose-300
                           disabled:opacity-50 transition"
              >
                <Icon name="delete" size={12} /> Remove
              </button>
            )}
            <input
              ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onPickFile(f);
                e.target.value = '';
              }}
            />
          </div>
        </div>
      </div>
    </motion.div>
  );
}
